// src/utils/dealerUtils.js

import axios from 'axios';
import Config from '../utils/Config';
import confetti from "canvas-confetti";

// Utility function to format phone numbers
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return 'N/A'; // Return 'N/A' if no phone number is provided

  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  if (cleaned.length === 10) {
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumber;
  }
  return phoneNumber;
};

// Function to calculate expiration date
export const calculateExpirationDate = (licXprdte) => {
  if (!licXprdte || licXprdte.length !== 2) return 'Invalid Expiration Date';

  const currentYear = new Date().getFullYear();
  const currentDecade = Math.floor(currentYear / 10) * 10;

  const yearLastDigit = parseInt(licXprdte[0], 10);
  let year = currentDecade + yearLastDigit;
  if (year < currentYear) year += 10;

  const monthMapping = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6,
    G: 7, H: 8, J: 9, K: 10, L: 11, M: 12,
  };
  const month = monthMapping[licXprdte[1]?.toUpperCase()];
  if (!month) return 'Invalid Expiration Date';

  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });
};

// Function to map LIC_REGN to IRS Region description
export const getIRSRegion = (licRegn) => {
  const regionMapping = {
    "1": "Southeast",
    "3": "Midwest",
    "4": "Central",
    "5": "Southwest",
    "6": "North Atlantic",
    "8": "Mid Atlantic",
    "9": "Western",
  };

  return regionMapping[licRegn] || "Unknown Region";
};

// Function to check if a date is expired
export const isDateExpired = (licXprdte) => {
  if (!licXprdte || licXprdte.length !== 2) return false;

  const currentYear = new Date().getFullYear();
  const currentDecade = Math.floor(currentYear / 10) * 10;

  const yearLastDigit = parseInt(licXprdte[0], 10);
  let year = currentDecade + yearLastDigit;
  if (year < currentYear) year += 10;

  const monthMapping = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6,
    G: 7, H: 8, J: 9, K: 10, L: 11, M: 12,
  };
  const month = monthMapping[licXprdte[1]?.toUpperCase()];
  if (!month) return false;

  // Calculate the expiration date as the end of the month
  const expirationDate = new Date(year, month, 0); // Last day of the month
  const today = new Date();

  // Check if today's date is past the expiration date
  return today > expirationDate;
};

export const loadGoogleMapsAPI = (apiKey) => {
  return new Promise((resolve, reject) => {
    if (window.google) {
      resolve(window.google); // API is already loaded
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Google Maps API failed to load.'));

    document.head.appendChild(script);
  });
};

/**
* Generic function to make API requests using Axios
* @param {string} endpoint - API endpoint (relative to the base URL)
* @param {object} options - Configuration options for the request (method, params, body, etc.)
* @returns {Promise<any>} - The API response data
*/
export const fetchFromAPI = async (endpoint, options = {}) => {
  try {
    const { method = 'GET', params = {}, body = {}, headers = {}, includeCredentials = false } = options;

    const response = await axios({
      method,
      url: `${Config.API.API_BASE_URL}${endpoint}`,
      params,
      data: body,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      withCredentials: includeCredentials,
    });

    return response;
  } catch (error) {
    // console.error('Error during API request:', error);
    throw error;
  }
};

// ✅ Format Date Function
export const formatDate = (dateString) => {
  if (!dateString) return "Unknown";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// ✅ Format Date + Time Function
export const formatDateTime = (dateString) => {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",   // e.g. Jul
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,     // 12-hour format with AM/PM
  });
};

export const resizeAndCompress = (file, maxSize = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      if (width > height && width > maxSize) {
        height *= maxSize / width;
        width = maxSize;
      } else if (height > width && height > maxSize) {
        width *= maxSize / height;
        height = maxSize;
      } else if (width > maxSize) {
        width = maxSize;
        height = maxSize;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => resolve(blob),
        file.type === "image/png" ? "image/png" : "image/jpeg",
        quality
      );
    };
    img.src = URL.createObjectURL(file);
  });
};

// converts "09:00" -> "9:00 AM", "21:15" -> "9:15 PM"
export const to12h = time24 => {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1;       // 0->12, 13->1
  return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
};

export const fireConfetti = () => {
  const duration = 1500;
  const end = Date.now() + duration;

  const frame = () => {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
    });

    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  };

  frame();
};

export const scorePassword = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
};

export const displayMessage = (messageRef, message, style, setMessageClass) => {
  if (messageRef.current) {
    messageRef.current.style.display = "none";
    void messageRef.current.offsetHeight; // force reflow
    messageRef.current.innerHTML = message;
    messageRef.current.style.display = "block";
    if (typeof setMessageClass === 'function') {
      setMessageClass(style);
    }
    // scrollToMessage(messageRef);    
  }
};

export const scrollToMessage = (messageRef) => {
  if (messageRef.current) {
    messageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    messageRef.current.focus();
  }
};

export const getWebsiteAddress = (subdomain) => {
  if (subdomain && subdomain !== "yourname" && Config.API.WEBSITE_BASE_URL) {
    return Config.API.WEBSITE_BASE_URL.replace("<sitename>", subdomain);
  }
};

export const viewWebsite = (subdomain, viewToken, status, claimId, siteStatus, publiclyAvailable) => {
  if (subdomain && subdomain !== "yourname" && Config.API.WEBSITE_BASE_URL) {
    const dealerURL = status === "Approved" && siteStatus === "Published" && publiclyAvailable !== false
      ? getWebsiteAddress(subdomain)
      : claimId ? new URL('/website-preview/' + encodeURIComponent(claimId), window.location.origin).href : null;
    if (dealerURL) window.open(dealerURL, "_blank", "noopener,noreferrer");
  } else {
    console.warn("Invalid or missing subdomain.");
  }
};

// Verified FFL dates are calendar dates, not local-time instants. Keep legacy directory helpers separate.
const fflExpirationCalendarDay = (value) => {
  if (!(value instanceof Date) && (typeof value !== "string" || !value.trim())) return NaN;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) : NaN;
};

export const formatFflExpirationDate = (value) => {
  const day = fflExpirationCalendarDay(value);
  return Number.isFinite(day) ? new Date(day).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
  }) : "Unknown";
};

export const getFflExpirationDaysLeft = (value, now = new Date()) =>
  (fflExpirationCalendarDay(value) - fflExpirationCalendarDay(now)) / 86400000;

export const getFflExpirationClass = (value, now = new Date()) => {
  const daysLeft = getFflExpirationDaysLeft(value, now);
  if (!Number.isFinite(daysLeft)) return "expiration-unknown";
  if (daysLeft < 0) return "expiration-expired";
  if (daysLeft <= 30) return "expiration-critical";
  if (daysLeft <= 90) return "expiration-warning";
  return "expiration-ok";
};

export const getDaysLeft = (expirationDate) => {
  const today = new Date();
  const expDate = new Date(expirationDate);
  return Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
};

export const getExpirationClass = (expirationDate) => {
  if (!expirationDate) return "expiration-unknown";

  const today = new Date();
  const expDate = new Date(expirationDate);
  const diffTime = expDate - today;
  const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (daysLeft <= 0) return "expiration-expired";      // expired
  if (daysLeft <= 30) return "expiration-critical";    // <= 30 days
  if (daysLeft <= 90) return "expiration-warning";     // <= 90 days
  return "expiration-ok";                              // > 90 days
};

export const getFileExtension = (filePath) => {
  if (!filePath) return '';
  return filePath.split('.').pop().toUpperCase();
};

export const getFileIcon = (ext) => {
  switch (ext.toLowerCase()) {
    case 'pdf': return '📄';
    case 'jpg':
    case 'jpeg':
    case 'png': return '🖼️';
    default: return '📎';
  }
};

export const normalizeUrl = (site) => {
  const s = site?.trim() || "";
  if (!s) return "";

  // already has scheme
  if (s.includes("://")) return s;

  // if string contains a port (like :5173), use http
  if (/:\d+$/.test(s)) {
    return `http://${s}`;
  }

  // otherwise assume https
  return `https://${s}`;
};